import {
  createAppSelector,
  pageSelectors,
  sectionUserSelectors,
  selectCurrentUserId,
  userSelectors,
} from "./base";
import {
  selectDisabledUserIds,
  selectIsCurrentOrgUserDisabled,
  selectIsOrgUserDisabled,
} from "./org";
import type { EnabledSectionUser } from "./types";

import { getEnabledSectionRole, isSectionUserEnabled } from "utils/roles";

export const selectEnabledSectionUsers = createAppSelector(
  [sectionUserSelectors.selectAll, selectDisabledUserIds],
  (sectionUsers, disabledUserIds) =>
    sectionUsers.filter((sectionUser) =>
      isSectionUserEnabled(sectionUser, disabledUserIds.has(sectionUser.user_id)),
    ) as EnabledSectionUser[],
);

// Select list of SectionUsers for the current section

export const selectEnabledSectionUsersForCurrentSection = createAppSelector(
  [sectionUserSelectors.filterForCurrentSection, selectDisabledUserIds],
  (sectionUsers, disabledUserIds) =>
    sectionUsers.filter((sectionUser) =>
      isSectionUserEnabled(sectionUser, disabledUserIds.has(sectionUser.user_id)),
    ) as EnabledSectionUser[],
);

export const selectSectionUserIdBySectionId = createAppSelector(
  [sectionUserSelectors.filterForCurrentUser, (state, sectionId: string | null) => sectionId],
  (sectionUsers, sectionId) => sectionUsers.find((cu) => cu.section_id === sectionId)?.id || null,
);

export const selectSectionUserByUserIdMap = createAppSelector(
  [sectionUserSelectors.filterForCurrentSection],
  (sectionUsers) =>
    Object.fromEntries(sectionUsers.map((sectionUser) => [sectionUser.user_id, sectionUser])),
);

// Select a single SectionUser by userId in the current section
export const selectSectionUserByUserId = createAppSelector(
  [sectionUserSelectors.filterForCurrentSection, (state, userId: string | null) => userId],
  (sectionUsers, userId) => sectionUsers.find((cu) => cu.user_id === userId) || null,
);

export const selectSectionRoleByUserId = createAppSelector(
  [selectSectionUserByUserId, selectIsOrgUserDisabled],
  (sectionUser, isOrgUserDisabled) => getEnabledSectionRole(sectionUser, isOrgUserDisabled),
);

export const selectSectionUserIdByUserId = createAppSelector(
  [selectSectionUserByUserId],
  (sectionUser) => sectionUser?.id || null,
);

// Select the current SectionUser for the current user in the current section

export const selectCurrentSectionUser = createAppSelector(
  [sectionUserSelectors.filterForCurrentSection, selectCurrentUserId],
  (sectionUsers, userId) => sectionUsers.find((cu) => cu.user_id === userId) || null,
);

/**
 * Identical to `selectCurrentSectionUser`, but returns `null` if the section user is disabled.
 */
export const selectCurrentSectionUserIfEnabled = createAppSelector(
  [selectCurrentSectionUser, selectIsCurrentOrgUserDisabled],
  (sectionUser, isOrgUserDisabled) =>
    isSectionUserEnabled(sectionUser, isOrgUserDisabled) ? sectionUser : null,
);

export const selectCurrentUserIsEnabledForSection = createAppSelector(
  [selectCurrentSectionUserIfEnabled],
  (enabledSectionUser) => Boolean(enabledSectionUser),
);

export const selectCurrentSectionUserId = createAppSelector(
  [selectCurrentSectionUser],
  (sectionUser) => sectionUser?.id || null,
);

// In most cases, you should use capabilities (canAuthorSection, canParticipateInSection, etc.) instead of roles
// (student, instructor, etc.) to determine what the user can do. For example, instead of checking if the current
// course role is "student", you should check if the user can participate in the course.
export const selectCurrentSectionRole = createAppSelector(
  [selectCurrentSectionUserIfEnabled],
  (sectionUser) => sectionUser?.role || null,
);

// Select the role of a SectionUser by sectionId

export const selectSectionRoleBySectionId = createAppSelector(
  [
    sectionUserSelectors.filterForCurrentUser,
    selectIsCurrentOrgUserDisabled,
    (state, sectionId: string | null) => sectionId,
  ],
  (sectionUsers, isOrgUserDisabled, sectionId) =>
    getEnabledSectionRole(
      sectionUsers.find((sectionUser) => sectionUser.section_id === sectionId) ?? null,
      isOrgUserDisabled,
    ),
);

export const selectEnabledSectionUsersByPageId = createAppSelector(
  [pageSelectors.selectById, selectEnabledSectionUsers],
  (page, enabledSectionUsers) =>
    enabledSectionUsers.filter((cu) => cu.section_id === page?.section_id),
);

export const selectUserBySectionUserId = createAppSelector(
  [sectionUserSelectors.selectById, userSelectors.selectEntities],
  (sectionUser, users) =>
    sectionUser?.user_id && sectionUser.user_id in users ? users[sectionUser.user_id] : null,
);

export const selectSectionRoleBySectionUserId = createAppSelector(
  [sectionUserSelectors.selectById],
  (sectionUser) => sectionUser?.role || null,
);
